import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QUOTE } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_TYPES = [
  "Small retail parcel",
  "Private courier",
  "Medical / Pharmaceutical",
  "Other",
];

const TIME_WINDOWS = [
  "Morning (8a–12p)",
  "Afternoon (12p–5p)",
  "Evening (5p–8p)",
  "Flexible",
];

const FREQUENCIES = ["Once", "Daily", "Weekly", "Monthly"];

function Field({ label, testId, value, onChange, type = "text", error, placeholder, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
        {label}{required && "*"}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest text-base placeholder:text-charcoal/30"
      />
      {error && <div className="mt-1 text-xs text-terracotta">{error}</div>}
    </div>
  );
}

export default function QuotePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    pickup_address: "",
    dropoff_address: "",
    package_type: "",
    weight_lbs: "",
    requested_date: "",
    time_window: "",
    frequency: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "We need an email to reply.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.phone.trim()) e.phone = "Please enter a phone number.";
    if (!form.pickup_address.trim()) e.pickup_address = "Pickup address is required.";
    if (!form.dropoff_address.trim()) e.dropoff_address = "Dropoff address is required.";
    if (!form.package_type) e.package_type = "Choose a package type.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setStatus("loading");
    try {
      const payload = {
        ...form,
        business_name: form.business_name || null,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : null,
        requested_date: form.requested_date || null,
        time_window: form.time_window || null,
        frequency: form.frequency || null,
        notes: form.notes || null,
      };
      await axios.post(`${API}/quote`, payload);
      setStatus("done");
      toast.success("Quote request received — we'll be in touch shortly.");
    } catch (err) {
      setStatus("error");
      toast.error("Couldn't send just now — please try again.");
    }
  };

  if (status === "done") {
    return (
      <main className="pt-10 md:pt-12 pb-24 bg-paper min-h-[80vh]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest text-cream mb-8"
          >
            <CheckCircle2 className="w-8 h-8" strokeWidth={1.4} />
          </motion.div>
          <h1
            data-testid={QUOTE.success}
            className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-forest"
          >
            Your quote is <span className="italic font-light text-terracotta">on the way</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg text-charcoal/70 max-w-xl mx-auto">
            A Waylio dispatcher will review your request and reply within one
            business day with pricing, driver availability, and a pickup ETA.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-forest hover:bg-terracotta text-cream px-6 py-3 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back home
            </Link>
            <a
              href="mailto:support@waylio.com"
              className="inline-flex items-center gap-2 rounded-full border border-forest/30 text-forest px-6 py-3 text-sm font-medium hover:bg-forest hover:text-cream transition-colors"
            >
              Reply to support@waylio.com
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={QUOTE.root} className="pt-10 md:pt-12 pb-24 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-charcoal/60 hover:text-forest text-xs uppercase tracking-[0.25em] mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left — brief */}
          <div className="col-span-12 lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
              Get a quote
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-forest">
              Tell us what you'd like <span className="italic font-light">delivered</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-charcoal/70 max-w-md">
              A real Waylio dispatcher reviews every request personally and
              replies within one business day with pricing, an assigned driver,
              and a pickup ETA.
            </p>

            <div className="mt-12 space-y-6">
              {[
                ["01", "Send this form", "It takes about a minute."],
                ["02", "We call or email back", "Quote and driver within one business day."],
                ["03", "Delivery + proof", "Photo, signature, or receipt to your inbox."],
              ].map(([n, t, s]) => (
                <div key={n} className="flex items-start gap-4">
                  <div className="chapter-num text-3xl text-terracotta w-10 shrink-0">
                    {n}
                  </div>
                  <div>
                    <div className="font-serif text-lg text-forest leading-tight">
                      {t}
                    </div>
                    <div className="text-sm text-charcoal/70 mt-1">{s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={onSubmit}
            noValidate
            className="col-span-12 lg:col-span-7 bg-cream border border-forest/15 p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <Field required label="Name" testId={QUOTE.name} value={form.name} onChange={(v) => setField("name", v)} error={errors.name} />
              <Field label="Business name" testId={QUOTE.business} value={form.business_name} onChange={(v) => setField("business_name", v)} />
              <Field required label="Email" type="email" testId={QUOTE.email} value={form.email} onChange={(v) => setField("email", v)} error={errors.email} />
              <Field required label="Phone" type="tel" testId={QUOTE.phone} value={form.phone} onChange={(v) => setField("phone", v)} error={errors.phone} />
            </div>

            <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <Field required label="Pickup address" testId={QUOTE.pickup} value={form.pickup_address} onChange={(v) => setField("pickup_address", v)} error={errors.pickup_address} placeholder="Street, City, ZIP" />
              <Field required label="Dropoff address" testId={QUOTE.dropoff} value={form.dropoff_address} onChange={(v) => setField("dropoff_address", v)} error={errors.dropoff_address} placeholder="Street, City, ZIP" />

              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
                  Package type*
                </label>
                <Select value={form.package_type} onValueChange={(v) => setField("package_type", v)}>
                  <SelectTrigger
                    data-testid={QUOTE.packageType}
                    className="w-full bg-transparent border-0 border-b border-forest/30 rounded-none px-0 h-10 focus:ring-0 focus-visible:ring-0 focus:border-terracotta text-forest text-base"
                  >
                    <SelectValue placeholder="Choose type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PACKAGE_TYPES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.package_type && (
                  <div className="mt-1 text-xs text-terracotta">{errors.package_type}</div>
                )}
              </div>

              <Field label="Weight (lbs, optional)" type="number" testId={QUOTE.weight} value={form.weight_lbs} onChange={(v) => setField("weight_lbs", v)} placeholder="e.g. 12" />
            </div>

            <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <Field label="Requested date" type="date" testId={QUOTE.date} value={form.requested_date} onChange={(v) => setField("requested_date", v)} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
                  Time window
                </label>
                <Select value={form.time_window} onValueChange={(v) => setField("time_window", v)}>
                  <SelectTrigger
                    data-testid={QUOTE.timeWindow}
                    className="w-full bg-transparent border-0 border-b border-forest/30 rounded-none px-0 h-10 focus:ring-0 focus-visible:ring-0 focus:border-terracotta text-forest text-base"
                  >
                    <SelectValue placeholder="Choose a window" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_WINDOWS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
                  How often?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FREQUENCIES.map((f) => {
                    const active = form.frequency === f;
                    return (
                      <button
                        key={f}
                        type="button"
                        data-testid={`${QUOTE.frequency}-${f.toLowerCase()}`}
                        onClick={() => setField("frequency", active ? "" : f)}
                        className={`py-3 px-4 text-sm border transition-colors ${
                          active
                            ? "border-terracotta bg-terracotta text-cream"
                            : "border-forest/25 text-forest hover:border-terracotta"
                        }`}
                      >
                        {f}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 text-[11px] text-charcoal/55">
                  Weekly &amp; monthly pickups get a preferred, same-driver rate.
                </div>
              </div>
            </div>

            <div className="mt-6 md:mt-8">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-charcoal/60 mb-2">
                Notes (fragile? refrigeration? gate code?)
              </label>
              <textarea
                data-testid={QUOTE.notes}
                rows={4}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                className="w-full bg-transparent border-0 border-b border-forest/30 focus:border-terracotta outline-none py-2 text-forest resize-none"
                placeholder="Anything the driver should know."
              />
            </div>

            <div className="mt-10 flex items-center justify-between gap-4">
              <p className="text-[11px] text-charcoal/50 max-w-sm">
                We reply personally, usually within one business day. We don't share your info.
              </p>
              <button
                type="submit"
                data-testid={QUOTE.submit}
                disabled={status === "loading"}
                className="group inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-cream px-6 py-3.5 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Request quote
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </main>
  );
}
